import { useQuery } from "@tanstack/react-query";
import type {
  CreateLibraryInput,
  EpisodeRef,
  LibraryKind,
  PatchLibraryInput,
  ProjectDetail,
  StoryboardView,
  Term,
  WorldView,
} from "@dramapie/shared";
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import { useSearchParams } from "react-router-dom";

import {
  ApiError,
  createLibraryEntity,
  deleteLibraryEntity,
  getLibrary,
  getProject,
  getStoryboard,
  getTerms,
  getWorld,
  patchLibraryEntity,
  uploadLibraryRef,
} from "../../lib/api";
import { errorMessage } from "../../lib/errors";
import { formatDate } from "../../lib/format";
import {
  LIBRARY_TAB_LABELS,
  LIBRARY_TAB_ORDER,
  type LibraryTab,
} from "../../lib/library-tabs";
import {
  LIBRARY_TABS,
  entityDescription,
  entityRoleLabel,
  entityTimeTags,
  initialOf,
  libraryRefCount,
  libraryRefUrl,
  referencedByFromDetails,
  referencedByText,
  type LibraryEntityView,
  type ReferenceRef,
} from "../../lib/m5";
import { useImageViewer } from "../media/ImageViewer";
import { EmptyState, ErrorState, LoadingState } from "../states";
import { WorldLibrary } from "../views/WorldLibrary";

/** 从镜头卡 chip 进来时定位到的条目：有 id 就精确选中，只有名字就按名字匹配。 */
export interface LibraryFocusTarget {
  kind: LibraryKind;
  id?: string | null;
  name?: string;
}

export interface LibraryDialogProps {
  projectId: string;
  open: boolean;
  onClose: () => void;
  /** 打开时定位到的实体（镜头卡的 角色/场景 chips）。 */
  focus?: LibraryFocusTarget | null;
  /** 集列表：把 409 里的 `episodeId` 翻成「第1集」。 */
  episodes?: readonly EpisodeRef[];
  /** 当前集：世界树的场景 join 用这一集的故事板；缺省读 `?ep=`，再退回第一集。 */
  episodeId?: string | null;
  onNotify: (kind: "success" | "error" | "info", message: string) => void;
  /** 父级已有项目 / 当前集故事板时传进来，省两次请求。 */
  project?: ProjectDetail | null;
  storyboard?: StoryboardView | null;
  /** 「在故事板看这一场」（世界 → 场景详情）。 */
  onOpenStoryboard?: (sceneId: string, episodeNo: number) => void;
  /** 空态里的「用 Agent 生成」。 */
  onGenerate?: () => void;
  /**
   * 渲染形态：
   * - `dialog`（默认）：资料库浮层，给镜头卡 chip / 世界节点等就地入口；
   * - `inline`：主区视图，无遮罩、无关闭按钮，由左栏「内容导航」进入。
   */
  variant?: "dialog" | "inline";
  /** 受控一级 tab（左栏二级导航 / 刷新还原）；不传则组件内部管理。 */
  tab?: LibraryTab;
  onTabChange?: (tab: LibraryTab) => void;
}

interface TopTabMeta {
  label: string;
  title: string;
  subtitle: string;
  createLabel: string | null;
}

/** L-01：一级 tab 固定为 世界 / 角色 / 道具 / 术语 / 资产版本（场景不单列）。 */
const TOP_TABS: Record<LibraryTab, TopTabMeta> = {
  world: {
    label: LIBRARY_TAB_LABELS.world,
    title: "世界",
    subtitle: "地点、世界规则与场景资产在同一棵树里；场景挂在节点下，带集数标签。",
    createLabel: null,
  },
  characters: {
    label: LIBRARY_TAB_LABELS.characters,
    title: "角色设定",
    subtitle: "角色是跨集复用的资产；出场统计与参考图在这里管理。",
    createLabel: "新建角色",
  },
  props: {
    label: LIBRARY_TAB_LABELS.props,
    title: "道具",
    subtitle: "道具按引用出现在镜头里；参考图与描述走同一条提交闸门。",
    createLabel: "新建道具",
  },
  terms: {
    label: LIBRARY_TAB_LABELS.terms,
    title: "术语表",
    subtitle: "术语来自 terms/terms.json，改名一次全项目同步。",
    createLabel: null,
  },
  assets: {
    label: LIBRARY_TAB_LABELS.assets,
    title: "资产与版本",
    subtitle: "资料库里的全部资产；版本链与引用数从这里直达。",
    createLabel: null,
  },
};

const TOP_TAB_ORDER: readonly LibraryTab[] = LIBRARY_TAB_ORDER;

type EditorState =
  | { mode: "create"; kind: LibraryKind }
  | { mode: "edit"; entity: LibraryEntityView };

interface FormValues {
  name: string;
  role: string;
  look: string;
  lookEn: string;
  time: string;
  notes: string;
}

const EMPTY_FORM: FormValues = { name: "", role: "", look: "", lookEn: "", time: "", notes: "" };

function valuesOfEntity(entity: LibraryEntityView): FormValues {
  return {
    name: entity.name,
    role: entity.role ?? "",
    look: entity.look ?? "",
    lookEn: entity.lookEn ?? "",
    time: entity.time.join("、"),
    notes: entity.notes ?? "",
  };
}

function splitTimeTags(raw: string): string[] {
  return [
    ...new Set(
      raw
        .split(/[,，、/\s]+/)
        .map((tag) => tag.trim().slice(0, 10))
        .filter(Boolean),
    ),
  ].slice(0, 6);
}

function toCreateInput(kind: LibraryKind, values: FormValues): CreateLibraryInput {
  const input: CreateLibraryInput = { kind, name: values.name.trim() };
  if (kind === "character" && values.role.trim()) input.role = values.role.trim();
  if (values.look.trim()) input.look = values.look.trim();
  if (kind === "character" && values.lookEn.trim()) input.lookEn = values.lookEn.trim();
  if (kind === "location") {
    const tags = splitTimeTags(values.time);
    if (tags.length > 0) input.time = tags;
  }
  if (values.notes.trim()) input.notes = values.notes.trim();
  return input;
}

function toPatchInput(kind: LibraryKind, values: FormValues): PatchLibraryInput {
  const { kind: _kind, ...patch } = toCreateInput(kind, values);
  return patch;
}

/** 搜索命中高亮（不改变文案，只包 `<mark>`）。 */
function highlight(text: string, query: string): ReactNode {
  const q = query.trim();
  if (!q) return text;
  const index = text.toLowerCase().indexOf(q.toLowerCase());
  if (index < 0) return text;
  return (
    <>
      {text.slice(0, index)}
      <mark>{text.slice(index, index + q.length)}</mark>
      {text.slice(index + q.length)}
    </>
  );
}

/** 参考图：有签名 URL 就显示图，没有就画首字占位（绝不猜 URL）。 */
function EntityThumb({ entity }: { entity: LibraryEntityView }) {
  const url = libraryRefUrl(entity);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [url]);

  if (url && !failed) {
    return (
      <div className="dp-lib-thumb">
        <img
          src={url}
          alt={`${entity.name} 参考图`}
          loading="lazy"
          onError={() => setFailed(true)}
        />
        {libraryRefCount(entity) > 1 ? (
          <span className="dp-lib-refcount" aria-hidden="true">
            {libraryRefCount(entity)} 图
          </span>
        ) : null}
      </div>
    );
  }

  return (
    <div className="dp-lib-thumb is-empty">
      <span className="dp-lib-initial" aria-hidden="true">
        {initialOf(entity.name)}
      </span>
      <span className="dp-lib-thumb-note">
        {entity.kind === "prop" ? "无参考图" : "参考图待上传"}
      </span>
    </div>
  );
}

function EntityCard({
  entity,
  active,
  blocked,
  deleting,
  uploading,
  episodes,
  query,
  usage,
  cardRef,
  onSelect,
  onEdit,
  onDelete,
  onUpload,
}: {
  entity: LibraryEntityView;
  active: boolean;
  blocked: ReferenceRef[] | undefined;
  deleting: boolean;
  uploading: boolean;
  episodes: readonly EpisodeRef[];
  query: string;
  /** 当前集故事板里的出场镜数；道具没有镜头级引用数据，给 null。 */
  usage: number | null;
  cardRef: (node: HTMLLIElement | null) => void;
  onSelect: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onUpload: (file: File) => void;
}) {
  const meta = LIBRARY_TABS[entity.kind];
  const role = entityRoleLabel(entity);
  const description = entityDescription(entity);
  const timeTags = entity.kind === "location" ? entityTimeTags(entity) : [];
  const isBlocked = blocked !== undefined;
  const imageViewer = useImageViewer();
  const refUrls = (entity.refUrls ?? []).filter((url) => /^https?:\/\//i.test(url));

  return (
    <li
      ref={cardRef}
      className="dp-lib-card"
      data-kind={entity.kind}
      data-active={active ? "true" : undefined}
      aria-current={active ? "true" : undefined}
    >
      <button
        type="button"
        className="dp-lib-card-main"
        aria-pressed={active}
        aria-label={`选中${meta.singular} ${entity.name}`}
        onClick={onSelect}
      >
        <EntityThumb entity={entity} />

        <span className="dp-lib-card-body">
          <span className="dp-lib-card-name">
            {highlight(entity.name, query)}
            {role ? <span className="dp-lib-card-role">{role}</span> : null}
          </span>

          {timeTags.length > 0 ? (
            <span className="dp-lib-card-time">
              {timeTags.map((tag) => (
                <span key={`${entity.id}-${tag}`}>{tag}</span>
              ))}
            </span>
          ) : null}

          {description ? (
            <span className="dp-lib-card-desc" title={description}>
              {highlight(description, query)}
            </span>
          ) : (
            <span className="dp-lib-card-desc is-empty">暂无描述</span>
          )}

          <span className="dp-lib-card-meta">
            <span>{usage === null ? "引用待补" : `${usage} 镜`}</span>
            <span title="LibraryEntity.versions[] 待后端补">版本 —</span>
          </span>
        </span>
      </button>

      {refUrls.length > 0 ? (
        // 缩略图在「选中卡片」按钮里，不能再嵌按钮：浮层小按钮单独放，点它只看图、不选中。
        <button
          type="button"
          className="dp-lib-zoom"
          aria-label={`放大查看${entity.name}参考图`}
          title="放大查看参考图（长按出放大镜）"
          onClick={(event) => {
            event.stopPropagation();
            imageViewer.open(
              refUrls.map((url, index) => ({
                src: url,
                title: `${entity.name} · 参考图${refUrls.length > 1 ? ` ${index + 1}` : ""}`,
                subtitle: meta.singular,
              })),
            );
          }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.5-3.5M11 8v6M8 11h6" />
          </svg>
        </button>
      ) : null}

      <div className="dp-lib-card-actions">
        <button
          type="button"
          className="dp-btn sm"
          aria-label={`编辑${meta.singular} ${entity.name}`}
          onClick={(event) => {
            event.stopPropagation();
            onEdit();
          }}
        >
          编辑
        </button>

        <label className="dp-btn sm dp-lib-upload">
          {uploading ? "上传中…" : "上传参考图"}
          <input
            type="file"
            accept="image/*"
            className="dp-sr"
            aria-label={`给${entity.name}上传参考图`}
            disabled={uploading}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) onUpload(file);
              // 允许重复选同一个文件。
              event.target.value = "";
            }}
          />
        </label>

        <button
          type="button"
          className="dp-btn sm danger"
          aria-label={`删除${meta.singular} ${entity.name}`}
          disabled={isBlocked || deleting}
          title={isBlocked ? "被镜头引用，先解除引用再删" : `删除${meta.singular}「${entity.name}」`}
          onClick={(event) => {
            event.stopPropagation();
            onDelete();
          }}
        >
          {deleting ? "删除中…" : "删除"}
        </button>
      </div>

      {blocked ? (
        <p role="status" className="dp-lib-blocked">
          <span className="dp-lib-blocked-mark" aria-hidden="true">
            ✕
          </span>
          {referencedByText(blocked, episodes)}
          <span className="dp-lib-blocked-hint">先解除引用再删</span>
        </p>
      ) : null}
    </li>
  );
}

function EntityEditor({
  editor,
  saving,
  onCancel,
  onSubmit,
}: {
  editor: EditorState;
  saving: boolean;
  onCancel: () => void;
  onSubmit: (values: FormValues) => void;
}) {
  const kind = editor.mode === "create" ? editor.kind : editor.entity.kind;
  const meta = LIBRARY_TABS[kind];
  const [values, setValues] = useState<FormValues>(() =>
    editor.mode === "edit" ? valuesOfEntity(editor.entity) : EMPTY_FORM,
  );
  const nameRef = useRef<HTMLInputElement | null>(null);
  const titleId = useId();

  useEffect(() => {
    const timer = setTimeout(() => nameRef.current?.focus(), 0);
    return () => clearTimeout(timer);
  }, []);

  function set<K extends keyof FormValues>(key: K, value: FormValues[K]) {
    setValues((prev) => ({ ...prev, [key]: value }));
  }

  const canSave = values.name.trim().length > 0 && !saving;

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!canSave) return;
    onSubmit(values);
  }

  return (
    <form className="dp-lib-editor" onSubmit={handleSubmit} aria-labelledby={titleId}>
      <div className="dp-lib-editor-head">
        <h3 id={titleId} className="dp-lib-editor-title">
          {editor.mode === "create" ? meta.createLabel : `编辑${meta.singular} · ${editor.entity.name}`}
        </h3>
        <button type="button" className="dp-btn sm ghost" onClick={onCancel} disabled={saving}>
          取消
        </button>
      </div>

      <div className="dp-lib-fields">
        <label className="dp-lib-field">
          <span className="dp-label">名字</span>
          <input
            ref={nameRef}
            className="dp-input"
            value={values.name}
            maxLength={40}
            required
            placeholder={kind === "character" ? "江砚" : kind === "location" ? "膳房" : "密契"}
            onChange={(event) => set("name", event.target.value)}
          />
        </label>

        {kind === "character" ? (
          <label className="dp-lib-field">
            <span className="dp-label">定位</span>
            <input
              className="dp-input"
              value={values.role}
              maxLength={20}
              placeholder="主角 / 配角 / 龙套"
              onChange={(event) => set("role", event.target.value)}
            />
          </label>
        ) : null}

        {kind === "location" ? (
          <label className="dp-lib-field">
            <span className="dp-label">时间标签</span>
            <input
              className="dp-input"
              value={values.time}
              placeholder="清晨、日、夜（最多 6 个）"
              onChange={(event) => set("time", event.target.value)}
            />
          </label>
        ) : null}

        <label className="dp-lib-field is-wide">
          <span className="dp-label">{kind === "prop" ? "描述" : "中文外形描述"}</span>
          <textarea
            className="dp-input dp-lib-textarea"
            value={values.look}
            maxLength={500}
            rows={2}
            placeholder={
              kind === "character"
                ? "二十七八，清瘦，青灰直裰，袖口磨白"
                : kind === "location"
                  ? "外门膳房，一排灶房烟囱"
                  : "铜制，边缘磨圆"
            }
            onChange={(event) => set("look", event.target.value)}
          />
        </label>

        {kind === "character" ? (
          <label className="dp-lib-field is-wide">
            <span className="dp-label">英文外形描述</span>
            <textarea
              className="dp-input dp-lib-textarea"
              value={values.lookEn}
              maxLength={500}
              rows={2}
              placeholder="a lean scholar in his late twenties…"
              onChange={(event) => set("lookEn", event.target.value)}
            />
          </label>
        ) : null}

        <label className="dp-lib-field is-wide">
          <span className="dp-label">备注</span>
          <input
            className="dp-input"
            value={values.notes}
            maxLength={1000}
            onChange={(event) => set("notes", event.target.value)}
          />
        </label>
      </div>

      <div className="dp-lib-editor-actions">
        <span className="dp-tiny-note">
          {editor.mode === "create" ? `新建后可在镜头里引用这个${meta.singular}` : "改动走提交闸门"}
        </span>
        <span className="dp-lib-grow" />
        <button type="submit" className="dp-btn sm primary" disabled={!canSave}>
          {saving ? "保存中…" : editor.mode === "create" ? "新建" : "保存"}
        </button>
      </div>
    </form>
  );
}

/**
 * 资料库抽屉（左轨「资料库」/ 镜头卡 chips 打开）。
 *
 * L-01 一级 tab：**世界 / 角色 / 道具 / 术语 / 资产版本**（场景不单列）。
 * 世界 tab 内是 地点 / 世界规则 二级切换，数据来自 M7 `WorldView`。
 * 删除被引用的实体时展示服务端 409 的 `details.referencedBy` 并**禁用删除**。
 */
export function LibraryDialog({
  projectId,
  open,
  onClose,
  focus,
  episodes = [],
  onNotify,
  episodeId: episodeIdProp,
  project: projectProp,
  storyboard: storyboardProp,
  onOpenStoryboard,
  onGenerate,
  variant = "dialog",
  tab: tabProp,
  onTabChange,
}: LibraryDialogProps) {
  const titleId = useId();
  const tabPanelId = useId();
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const restoreRef = useRef<HTMLElement | null>(null);
  const cardNodes = useRef(new Map<string, HTMLLIElement>());
  const tabNodes = useRef(new Map<LibraryTab, HTMLButtonElement>());

  const [internalTab, setInternalTab] = useState<LibraryTab>("world");
  const tab = tabProp ?? internalTab;

  /** 受控 / 非受控都走这里：左栏二级导航能直接切到角色、术语等。 */
  function selectTab(next: LibraryTab) {
    if (tabProp === undefined) setInternalTab(next);
    onTabChange?.(next);
  }

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<ReadonlySet<string>>(new Set());
  const [uploading, setUploading] = useState<ReadonlySet<string>>(new Set());
  const [query, setQuery] = useState("");
  /** 资产版本「查看」→ 世界节点（L-11 行内直达）。 */
  const [assetFocusNodeId, setAssetFocusNodeId] = useState<string | null>(null);
  /** 服务端 409 的引用清单：出现即禁用该条目的删除。 */
  const [blocked, setBlocked] = useState<Record<string, ReferenceRef[]>>({});

  const [searchParams] = useSearchParams();

  const libraryQuery = useQuery({
    queryKey: ["library", projectId],
    queryFn: () => getLibrary(projectId, { kind: "all" }),
    enabled: open && projectId.length > 0,
    staleTime: 5_000,
    retry: false,
  });
  const items = useMemo(() => libraryQuery.data ?? [], [libraryQuery.data]);

  const projectQuery = useQuery({
    queryKey: ["project", projectId],
    queryFn: () => getProject(projectId),
    enabled: open && projectProp === undefined && projectId.length > 0,
    staleTime: 30_000,
    retry: false,
  });
  const project = projectProp ?? projectQuery.data ?? null;

  const firstEpisodeId =
    episodeIdProp ?? searchParams.get("ep") ?? project?.episodes[0]?.id ?? null;
  const storyboardQuery = useQuery({
    queryKey: ["storyboard", projectId, firstEpisodeId ?? "pending"],
    queryFn: () => getStoryboard(projectId, { episode: firstEpisodeId ?? undefined }),
    enabled: open && storyboardProp === undefined && firstEpisodeId !== null,
    staleTime: 10_000,
    retry: false,
  });
  const storyboard = storyboardProp !== undefined ? storyboardProp : (storyboardQuery.data ?? null);

  const worldQuery = useQuery({
    queryKey: ["world", projectId],
    queryFn: () => getWorld(projectId),
    enabled: open && projectId.length > 0,
    staleTime: 15_000,
    retry: false,
  });
  const termsQuery = useQuery({
    queryKey: ["terms", projectId],
    queryFn: () => getTerms(projectId),
    enabled: open && projectId.length > 0,
    staleTime: 30_000,
    retry: false,
  });

  const world: WorldView | null = worldQuery.data ?? null;
  const terms: Term[] = termsQuery.data?.items ?? [];
  const missingWorld =
    worldQuery.isError &&
    worldQuery.error instanceof ApiError &&
    worldQuery.error.status === 404;
  const missingTerms =
    termsQuery.isError &&
    termsQuery.error instanceof ApiError &&
    termsQuery.error.status === 404;

  const counts = useMemo(() => {
    const next: Record<LibraryKind, number> = { character: 0, location: 0, prop: 0 };
    for (const item of items) next[item.kind] += 1;
    return next;
  }, [items]);

  const tabCounts = useMemo<Record<LibraryTab, number>>(
    () => ({
      world: world?.nodes.length ?? 0,
      characters: counts.character,
      props: counts.prop,
      terms: terms.length,
      assets: items.length,
    }),
    [world, counts, terms.length, items.length],
  );

  /**
   * 场景 chip / 资产版本「查看」→ 世界节点：
   * 先按场景名在 storyboard 里找 scene.id，再找直接引用它的节点。
   */
  const worldFocus = useMemo(() => {
    if (!focus || focus.kind !== "location" || !world) return null;
    const scene = storyboard?.scenes.find(
      (item) => item.name === focus.name || item.id === focus.id,
    );
    const node =
      (scene ? world.nodes.find((item) => item.sceneIds.includes(scene.id)) : undefined) ??
      world.nodes.find((item) => item.name === focus.name) ??
      null;
    return { nodeId: node?.id ?? null, sceneId: scene?.id ?? null };
  }, [focus, world, storyboard]);

  const worldKey = [
    worldFocus?.nodeId ?? "none",
    worldFocus?.sceneId ?? "none",
    assetFocusNodeId ?? "none",
  ].join("|");

  const activeKind: LibraryKind | null =
    tab === "characters" ? "character" : tab === "props" ? "prop" : null;

  const searchable = useMemo(() => {
    if (!activeKind) return [];
    const q = query.trim().toLowerCase();
    return items.filter((item) => {
      if (item.kind !== activeKind) return false;
      if (!q) return true;
      const haystack = `${item.name} ${item.role ?? ""} ${item.look ?? ""} ${item.notes ?? ""}`
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [items, activeKind, query]);

  const filteredTerms = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return terms;
    return terms.filter((term) =>
      `${term.term} ${term.aliases.join(" ")} ${term.category} ${term.definition}`
        .toLowerCase()
        .includes(q),
    );
  }, [terms, query]);

  /** 角色在当前集故事板里的出场镜数（`shots[].chars`），不引用就是 0。 */
  const usageByCharacter = useMemo(() => {
    const counts = new Map<string, number>();
    for (const shot of storyboard?.shots ?? []) {
      for (const charId of shot.chars) counts.set(charId, (counts.get(charId) ?? 0) + 1);
    }
    return counts;
  }, [storyboard]);

  const assetRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const kindLabel: Record<LibraryKind, string> = {
      character: "角色",
      location: "地点",
      prop: "道具",
    };
    return items
      .filter((item) => !q || item.name.toLowerCase().includes(q))
      .map((item) => ({
        id: item.id,
        kind: item.kind,
        name: item.name,
        typeLabel: kindLabel[item.kind],
        refCount: item.refs.length,
        updatedAt: item.updatedAt,
      }));
  }, [items, query]);

  // 浮层：焦点交接 + Esc 关闭（与用量弹层同一套键盘行为）。
  // 主区 inline 形态不是模态：没有遮罩 / 关闭按钮 / Esc，也不抢焦点。
  useEffect(() => {
    if (!open || variant !== "dialog") return;
    const active = document.activeElement;
    restoreRef.current = active instanceof HTMLElement ? active : null;
    const timer = setTimeout(() => closeRef.current?.focus(), 0);

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      event.preventDefault();
      onClose();
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("keydown", handleKeyDown);
      restoreRef.current?.focus();
    };
  }, [open, onClose, variant]);

  // 打开时按 focus 定位：场景 → 世界 tab；角色/道具 → 对应 tab。
  // 只应用一次（引用同一组 focus 时不因列表刷新把用户后来的选择拽回去）。
  const appliedFocus = useRef<string | null>(null);
  useEffect(() => {
    if (!open) {
      appliedFocus.current = null;
      return;
    }
    if (!focus) return;
    const signature = `${focus.kind}|${focus.id ?? ""}|${focus.name ?? ""}`;
    if (appliedFocus.current === signature) return;
    if (focus.kind === "location") {
      selectTab("world");
      if (worldFocus?.nodeId || worldFocus?.sceneId) appliedFocus.current = signature;
      return;
    }
    const nextTab: LibraryTab = focus.kind === "prop" ? "props" : "characters";
    selectTab(nextTab);
    if (focus.id) {
      setSelectedId(focus.id);
      appliedFocus.current = signature;
      return;
    }
    if (!focus.name) {
      appliedFocus.current = signature;
      return;
    }
    const match = items.find((item) => item.kind === focus.kind && item.name === focus.name);
    if (match) {
      setSelectedId(match.id);
      appliedFocus.current = signature;
    }
  }, [open, focus, items, worldFocus]);

  // 选中项滚进可视区（抽屉内部滚动，不动整页）。
  useEffect(() => {
    if (!open || !selectedId) return;
    const node = cardNodes.current.get(selectedId);
    node?.scrollIntoView?.({ block: "nearest" });
  }, [open, selectedId, tab, searchable]);

  const setCardNode = useCallback(
    (id: string) => (node: HTMLLIElement | null) => {
      if (node) cardNodes.current.set(id, node);
      else cardNodes.current.delete(id);
    },
    [],
  );

  async function refresh() {
    await libraryQuery.refetch();
  }

  async function handleSubmit(values: FormValues) {
    if (!editor) return;
    setSaving(true);
    try {
      if (editor.mode === "create") {
        const created = await createLibraryEntity(
          projectId,
          toCreateInput(editor.kind, values),
        );
        onNotify("success", `已新建${LIBRARY_TABS[editor.kind].singular}「${values.name.trim()}」`);
        selectTab(editor.kind === "prop" ? "props" : "characters");
        if (created) setSelectedId(created.id);
      } else {
        const { kind, id, name } = editor.entity;
        await patchLibraryEntity(projectId, id, toPatchInput(kind, values));
        onNotify("success", `${LIBRARY_TABS[kind].singular}「${name}」已保存`);
        setSelectedId(id);
      }
      setEditor(null);
      await refresh();
    } catch (error) {
      onNotify("error", errorMessage(error, "保存失败，请稍后重试"));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(entity: LibraryEntityView) {
    if (blocked[entity.id]) return;
    const meta = LIBRARY_TABS[entity.kind];
    setDeleting((prev) => new Set(prev).add(entity.id));
    try {
      await deleteLibraryEntity(projectId, entity.id);
      onNotify("success", `已删除${meta.singular}「${entity.name}」`);
      if (selectedId === entity.id) setSelectedId(null);
      setBlocked((prev) => {
        const next = { ...prev };
        delete next[entity.id];
        return next;
      });
      await refresh();
    } catch (error) {
      const refs =
        error instanceof ApiError ? referencedByFromDetails(error.details, error.payload) : [];
      if (error instanceof ApiError && error.status === 409) {
        setBlocked((prev) => ({ ...prev, [entity.id]: refs }));
        onNotify(
          "error",
          refs.length > 0
            ? `${meta.singular}「${entity.name}」${referencedByText(refs, episodes)}`
            : `${meta.singular}「${entity.name}」仍被镜头引用，不能删除`,
        );
      } else {
        onNotify("error", errorMessage(error, "删除失败，请稍后重试"));
      }
    } finally {
      setDeleting((prev) => {
        const next = new Set(prev);
        next.delete(entity.id);
        return next;
      });
    }
  }

  async function handleUpload(entity: LibraryEntityView, file: File) {
    const meta = LIBRARY_TABS[entity.kind];
    setUploading((prev) => new Set(prev).add(entity.id));
    try {
      await uploadLibraryRef(projectId, entity.id, file);
      onNotify("success", `${meta.singular}「${entity.name}」参考图已上传`);
      await refresh();
    } catch (error) {
      onNotify("error", errorMessage(error, "参考图上传失败"));
    } finally {
      setUploading((prev) => {
        const next = new Set(prev);
        next.delete(entity.id);
        return next;
      });
    }
  }

  function moveTab(direction: 1 | -1) {
    const index = TOP_TAB_ORDER.indexOf(tab);
    const next = TOP_TAB_ORDER[(index + direction + TOP_TAB_ORDER.length) % TOP_TAB_ORDER.length];
    if (!next) return;
    selectTab(next);
    tabNodes.current.get(next)?.focus();
  }

  /** 资产版本行「查看 →」：地点回世界节点，其余切到自己的 tab 并选中。 */
  function openAsset(row: { id: string; kind: LibraryKind; name: string }) {
    if (row.kind === "location") {
      const node = world?.nodes.find((item) => item.name === row.name) ?? null;
      setAssetFocusNodeId(node?.id ?? null);
      selectTab("world");
      return;
    }
    selectTab(row.kind === "prop" ? "props" : "characters");
    setSelectedId(row.id);
  }

  if (!open) return null;

  const activeTabId = `library-tab-${tab}`;
  const canCreate = projectId.length > 0 && TOP_TABS[tab].createLabel !== null;
  const meta = TOP_TABS[tab];

  const body = (
    <>
      <header className="dp-lib-head">
        <div>
          <p className="dp-label">library · 世界 / 角色 / 道具 / 术语 / 资产</p>
          <h2 id={titleId} className="dp-lib-title">
            资料库
          </h2>
          <p className="dp-lib-meta">
            {tabCounts.world} 世界节点 · {counts.character} 角色 · {counts.prop} 道具 ·{" "}
            {tabCounts.terms} 术语
          </p>
        </div>
        <div className="dp-lib-head-actions">
          {meta.createLabel ? (
            <button
              type="button"
              className="dp-btn sm primary"
              disabled={!canCreate || editor !== null}
              onClick={() =>
                setEditor({ mode: "create", kind: tab === "props" ? "prop" : "character" })
              }
            >
              {meta.createLabel}
            </button>
          ) : null}
          {variant === "dialog" ? (
            <button
              ref={closeRef}
              type="button"
              className="dp-icon-btn"
              aria-label="关闭资料库"
              onClick={onClose}
            >
              ×
            </button>
          ) : null}
        </div>
      </header>

      <div
        className="dp-lib-tabs"
        role="tablist"
        aria-label="资料库分类"
        onKeyDown={(event) => {
          if (event.key === "ArrowRight") {
            event.preventDefault();
            moveTab(1);
          } else if (event.key === "ArrowLeft") {
            event.preventDefault();
            moveTab(-1);
          }
        }}
      >
        {TOP_TAB_ORDER.map((id) => (
          <button
            key={id}
            type="button"
            role="tab"
            id={`library-tab-${id}`}
            ref={(node) => {
              if (node) tabNodes.current.set(id, node);
              else tabNodes.current.delete(id);
            }}
            aria-selected={tab === id}
            aria-controls={tabPanelId}
            tabIndex={tab === id ? 0 : -1}
            onClick={() => selectTab(id)}
          >
            {TOP_TABS[id].label}
            <span aria-hidden="true">{tabCounts[id]}</span>
          </button>
        ))}
      </div>

      {editor ? (
        <EntityEditor
          key={editor.mode === "create" ? `create-${editor.kind}` : editor.entity.id}
          editor={editor}
          saving={saving}
          onCancel={() => setEditor(null)}
          onSubmit={(values) => void handleSubmit(values)}
        />
      ) : null}

      <div
        role="tabpanel"
        id={tabPanelId}
        aria-labelledby={activeTabId}
        className={`dp-lib-panel ${tab === "world" ? "is-world" : ""}`}
      >
        {tab !== "world" ? (
          <div className="dp-lib-toolbar">
            <label className="dp-lib-search">
              <span aria-hidden="true">⌕</span>
              <input
                value={query}
                placeholder={
                  tab === "terms"
                    ? "搜索术语 / 别名 / 定义…"
                    : tab === "assets"
                      ? "搜索资产名…"
                      : "搜索名字 / 别名 / 作用…"
                }
                aria-label="搜索资料库"
                onChange={(event) => setQuery(event.target.value)}
              />
            </label>
            <span className="dp-tiny-note">
              {tab === "characters"
                ? `${searchable.length} / ${counts.character} 条`
                : tab === "props"
                  ? `${searchable.length} / ${counts.prop} 条`
                  : tab === "terms"
                    ? `${filteredTerms.length} / ${terms.length} 条`
                    : `${assetRows.length} / ${items.length} 条`}
            </span>
          </div>
        ) : null}

        {tab === "world" ? (
          worldQuery.isPending ? (
            <div className="dp-doc-loading">
              <LoadingState label="正在读取世界树…" />
            </div>
          ) : worldQuery.isError && !missingWorld ? (
            <ErrorState
              title="世界树加载失败"
              message="world/tree.json 的投影读取失败，请重试。"
              onRetry={() => void worldQuery.refetch()}
            />
          ) : world === null ? (
            <div className="dp-doc-empty">
              <p className="dp-doc-empty-title">还没有世界设定</p>
              <p className="dp-doc-empty-text">
                世界树来自 <code>world/tree.json</code>；老项目可能还没有这份文档。
              </p>
              {onGenerate ? (
                <button type="button" className="dp-btn sm primary" onClick={() => onGenerate()}>
                  用 Agent 生成
                </button>
              ) : null}
            </div>
          ) : (
            <WorldLibrary
              key={worldKey}
              world={world}
              storyboard={storyboard}
              outputs={project?.outputs ?? []}
              episodes={episodes.length > 0 ? episodes : (project?.episodes ?? [])}
              initialNodeId={worldFocus?.nodeId ?? assetFocusNodeId ?? null}
              initialSceneId={worldFocus?.sceneId ?? null}
              onOpenStoryboard={onOpenStoryboard}
              onGenerate={onGenerate}
              projectId={projectId}
              onNotify={onNotify}
            />
          )
        ) : tab === "characters" || tab === "props" ? (
          libraryQuery.isPending ? (
            <LoadingState label="正在读取资料库…" />
          ) : libraryQuery.isError ? (
            <ErrorState
              title="资料库加载失败"
              message={errorMessage(libraryQuery.error, "资料库加载失败")}
              onRetry={() => void libraryQuery.refetch()}
            />
          ) : searchable.length === 0 ? (
            <EmptyState
              title={
                query.trim()
                  ? "没有匹配项"
                  : LIBRARY_TABS[tab === "props" ? "prop" : "character"].emptyTitle
              }
              description={
                query.trim()
                  ? "试试清空搜索。"
                  : LIBRARY_TABS[tab === "props" ? "prop" : "character"].emptyHint
              }
              action={
                <button
                  type="button"
                  className="dp-btn sm primary"
                  onClick={() =>
                    setEditor({ mode: "create", kind: tab === "props" ? "prop" : "character" })
                  }
                >
                  {LIBRARY_TABS[tab === "props" ? "prop" : "character"].createLabel}
                </button>
              }
            />
          ) : (
            <ul
              className="dp-lib-grid"
              aria-label={`${LIBRARY_TABS[tab === "props" ? "prop" : "character"].label}列表`}
            >
              {searchable.map((entity) => (
                <EntityCard
                  key={entity.id}
                  entity={entity}
                  active={selectedId === entity.id}
                  blocked={blocked[entity.id]}
                  deleting={deleting.has(entity.id)}
                  uploading={uploading.has(entity.id)}
                  episodes={episodes}
                  query={query}
                  usage={
                    entity.kind === "character"
                      ? (usageByCharacter.get(entity.id) ?? 0)
                      : null
                  }
                  cardRef={setCardNode(entity.id)}
                  onSelect={() => setSelectedId(entity.id)}
                  onEdit={() => setEditor({ mode: "edit", entity })}
                  onDelete={() => void handleDelete(entity)}
                  onUpload={(file) => void handleUpload(entity, file)}
                />
              ))}
            </ul>
          )
        ) : tab === "terms" ? (
          termsQuery.isPending ? (
            <LoadingState label="正在读取术语…" />
          ) : termsQuery.isError && !missingTerms ? (
            <ErrorState
              title="术语加载失败"
              message="terms/terms.json 的投影读取失败，请重试。"
              onRetry={() => void termsQuery.refetch()}
            />
          ) : filteredTerms.length === 0 ? (
            <div className="dp-doc-empty">
              <p className="dp-doc-empty-title">还没有术语</p>
              <p className="dp-doc-empty-text">
                术语来自 <code>terms/terms.json</code>；新项目是空表，改名一次全项目同步。
              </p>
              {onGenerate ? (
                <button type="button" className="dp-btn sm primary" onClick={() => onGenerate()}>
                  用 Agent 生成
                </button>
              ) : null}
            </div>
          ) : (
            <table className="dp-lib-table">
              <thead>
                <tr>
                  <th>术语</th>
                  <th>分类</th>
                  <th>别名</th>
                  <th>定义</th>
                </tr>
              </thead>
              <tbody>
                {filteredTerms.map((term) => (
                  <tr key={term.id}>
                    <td className="dp-lt-name">{highlight(term.term, query)}</td>
                    <td>
                      <span className="dp-lib-layer">{term.category}</span>
                    </td>
                    <td className="dp-lt-mono">{term.aliases.join(" / ") || "—"}</td>
                    <td>{highlight(term.definition, query)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        ) : (
          <table className="dp-lib-table">
            <thead>
              <tr>
                <th>资产</th>
                <th>类型</th>
                <th>版本</th>
                <th>引用图</th>
                <th>更新时间</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {assetRows.map((row) => (
                <tr key={`${row.kind}-${row.id}`}>
                  <td className="dp-lt-name">{highlight(row.name, query)}</td>
                  <td>{row.typeLabel}</td>
                  <td className="dp-lt-mono" title="版本链待后端补 versions[]">
                    —
                  </td>
                  <td className="dp-lt-mono">{row.refCount}</td>
                  <td className="dp-lt-mono">{formatDate(row.updatedAt)}</td>
                  <td className="dp-lt-go">
                    <button
                      type="button"
                      className="dp-table-link"
                      aria-label={`查看 ${row.name}`}
                      onClick={() => openAsset(row)}
                    >
                      查看 →
                    </button>
                  </td>
                </tr>
              ))}
              {assetRows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="dp-lib-empty">
                    没有匹配的资产。
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        )}
      </div>

      <p className="dp-lib-foot">
        {tab === "world"
          ? "世界树来自 world/tree.json；场景不单列 tab，靠节点 sceneIds 关联到故事板场次。"
          : tab === "terms"
            ? "术语来自 terms/terms.json；定义只存一份，镜头里的用词跟着走。"
            : tab === "assets"
              ? "版本链待后端补 LibraryEntity.versions[]；当前显示现签参考图数与更新时间。"
              : "参考图走 R2（签名 URL 现签）；删除被镜头引用的条目会被服务端 409 拦下，并列出引用位置。"}
      </p>
    </>
  );

  if (variant === "inline") {
    return (
      <section
        aria-label="资料库"
        className={`dp-lib-inline${tab === "world" ? " is-world" : ""}`}
      >
        {body}
      </section>
    );
  }

  return (
    <div className="dp-scrim dp-lib-scrim" role="presentation" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={`dp-dialog dp-library${tab === "world" ? " is-world" : ""}`}
        onClick={(event) => event.stopPropagation()}
      >
        {body}
      </div>
    </div>
  );
}

/** 供测试 / 未来外部使用：一级 tab 顺序的唯一来源。 */
export const LIBRARY_TOP_TABS = TOP_TAB_ORDER;
